import { WorkMovementRestrictionsPageStore } from './work-movement-restrictions-page.store';
import { Component, computed, effect, inject, signal, viewChild, WritableSignal } from '@angular/core';
import moment from 'moment/moment';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { WorkGridItemObject } from '@it2go/types';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { getWorksFrag } from './work-movement-restrictions-page.query';
import { movementRestrictionTypeOptions } from '@libs/types/src/common/enum/movement-restriction-type.enum';
import {
    WorkDataMovementRestrictionObject,
} from '@libs/types/src/services/object-type/work/work-data-movement-restriction.object';
import { workUri } from 'packages/client/src/app/utils/router.utils';
import { TranslatePipe } from '@ngx-translate/core';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import {
    RealizedWorkPageHeaderComponent,
} from '../../../../../modules/content/pages/services/realized-work-pages/realized-work-page-header/realized-work-page-header.component';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import {
    WorkEmailHistoryTableColumnComponent,
} from '../../../../common-service/work/work-email-history-table-column/work-email-history-table-column.component';
import {
    WorkUniqueDaysPipe,
    WorkUniquesPipe,
} from '../../../../../modules/content/components/pipe/work/work-uniques.pipe';
import { SessionService } from '../../../../../services/session.service';
import { ConfirmBuilder } from '../../../../../modules/shared/service/confirm.builder';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { fb, routeParams, translate } from '../../../../../modules/shared/shared.global-service';
import { TableColumn, TableColumnType } from '../../../../../modules/shared/components/kit/table/table.types';
import {
    dateFromColumnWithTime,
    dateToColumnWithTime,
    folderPersonColumn,
    usersChipColumn,
} from '../../../../../modules/shared/components/kit/table/table.fields';
import { successDeleted } from '../../../../../modules/content/content.error';
import {
    MovementRestrictionSendEmailDialogComponent,
} from '../../movement-restriction-send-email-dialog/movement-restriction-send-email-dialog.component';
import { workHideDelete } from '../../../../medical-service/work/work-detail/utils/work.transform-utils';
import { WorkPrintService } from '../../../../medical-service/work/work-print.service';

@Component({
    selector: 'app-work-movement-restrictions-page',
    templateUrl: './work-movement-restrictions-page.component.html',
    styleUrl: './work-movement-restrictions-page.component.scss',
    providers: [WorkMovementRestrictionsPageStore],
    imports: [
        TranslatePipe,
        PrimeTemplate,
        RealizedWorkPageHeaderComponent,
        TableComponent,
        ButtonComponent,
        WorkEmailHistoryTableColumnComponent,
        WorkUniqueDaysPipe,
        WorkUniquesPipe,
    ],
})
export class WorkMovementRestrictionsPageComponent {

    protected readonly store = inject<WorkMovementRestrictionsPageStore>(WorkMovementRestrictionsPageStore);
    protected readonly fb = inject(FormBuilder);
    protected readonly router = inject(Router);
    protected readonly session = inject(SessionService);
    protected readonly confirm = inject(ConfirmBuilder);
    protected readonly dialogService = inject(BaseDialogService);
    protected readonly workPrint = inject(WorkPrintService);

    protected tableComponent = viewChild(TableComponent);

    protected form = fb.group({
        date: fb.control(moment().startOf('d').toDate()),
        endDate: fb.control<Date>(new Date()),
        selectedVisits: fb.control<string>('all'),
        selectedPeriod: fb.control<TimePeriodEnum>(TimePeriodEnum.Day, {
            nonNullable: true,
        }),
    });

    protected formChange = toSignal(this.form.valueChanges);

    protected dateFilter: WritableSignal<any> = signal([]);
    protected workplaceFilter: WritableSignal<any> = signal([]);
    protected extraFilters = computed(() => [
        [
            {
                value: [this.store.globalGlobalStore.organizationStore.selectedId()],
                column: 'organizationId',
                operator: 'EQ',
            },
        ],
        [
            {
                value: [WorkTypeEnum.MOVEMENT_RESTRICTION],
                column: 'workType',
                operator: 'EQ',
            },
        ],
        ...(this.isInFolder() ? [] : this.dateFilter()),
        ...(this.isInFolder() ? [] : this.workplaceFilter()),
        ...(this.isInFolder() ? [
            {
                value: [routeParams.folderId()],
                column: 'folderId',
                operator: 'IN',
            },
        ] : []),
    ]);

    protected isInFolder = computed(() => this.router.url.includes('/folders'));

    protected columns = computed<TableColumn[]>(() => {
        return [
            { name: 'print' },
            { name: 'send' },
            {
                name: 'workData.swdmr.sequenceNumber',
                dataKey: null,
                data: (item: WorkGridItemObject<WorkDataMovementRestrictionObject>) => `${String(item.workData?.sequenceNumber || 1)}/${new Date(item.dateFrom).getFullYear()}`,
                sortable: true,
            },
            dateFromColumnWithTime,
            dateToColumnWithTime,
            {
                name: 'workData.swdmr.type',
                dataKey: 'workData.type',
                data: (item: any) => item ? translate.instant(`workData.movementRestriction.type.${item}`) : null,
                filterable: {
                    type: 'select',
                    selectOptions: () => movementRestrictionTypeOptions,
                },
            },
            {
                name: 'workData.swdmr.location',
                dataKey: 'workData.location',
                filterable: true,
            },
            {
                name: 'workData.swdmr.data',
                dataKey: 'workData.data',
                type: TableColumnType.String50,
                filterable: true,
            },
            {
                name: 'workData.swdmr.witness',
                dataKey: 'workData.witness',
                filterable: true,
            },
            { name: 'emailHistory' },
            usersChipColumn,
            ...(this.isInFolder() ? [] : [folderPersonColumn]),
        ];
    });

    constructor() {
        effect(() => {
            const value = this.formChange();
            if (!value) return;

            if (value.date && value.endDate) {
                this.dateFilter.set([[
                    {
                        value: [
                            value.date.toISOString(),
                            value.endDate.toISOString(),
                        ],
                        column: 'dateFrom',
                        operator: 'BETWEEN',
                    },
                ]]);
            } else {
                this.dateFilter.set([]);
            }
            switch (value.selectedVisits) {
                case 'all':
                    this.workplaceFilter.set([]);
                    break;
                case 'mine':
                    this.workplaceFilter.set([[
                        {
                            column: 'userId',
                            operator: 'EQ',
                            value: [this.store.globalGlobalStore.loggedUserStore.currentUserId()],
                        },
                    ]]);
                    break;
                case 'mineWorkplace':
                    const workplaceFilter = this.store.globalGlobalStore.loggedUserStore.filterByWorkplaceIdOrServiceOfNonAssigned()();
                    this.workplaceFilter.set(workplaceFilter);
                    break;
            }
        });
    }

    protected async detail(item: WorkGridItemObject): Promise<void> {
        await this.router.navigateByUrl(workUri(item, item.serviceId));
    }

    protected delete(event: any): void {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteWork({ id: event.item.id });
                this.tableComponent()?.reload();
                successDeleted();
            },
            event,
        );
    }

    protected print(work: WorkGridItemObject<WorkDataMovementRestrictionObject>): void {
        this.workPrint.openRow(work);
    }

    protected send(work: WorkGridItemObject<WorkDataMovementRestrictionObject>): void {
        this.dialogService.open(
            MovementRestrictionSendEmailDialogComponent,
            {
                data: {
                    personId: work.person?.id,
                    folderId: work.folderId,
                    serviceId: work.serviceId,
                    workId: work.id,
                    isNew: false,
                },
            },
        ).onClose.subscribe(() => {
            this.tableComponent()?.reload();
        });
    }

    protected readonly workHideDelete = workHideDelete(this.store.globalGlobalStore);

    protected readonly getWorksFrag = getWorksFrag;

}
